import { useMemo, useState } from 'react';

import { Box, Button, Heading, Paragraph, Popover, TextButton } from '@zillow/constellation-10';

import { useRentalManagerUserPropertySummaryWebApiHandler_applications } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import selectPropertyUtils from '../../../../utils/selectPropertyUtils';

import { PERMISSION_REQUEST_TYPE } from '../../../../constants/ApplicationPermissionsConstants';
import { CLIPBOARD_ILLUSTRATION_URL } from '../../../../constants/DigitalLibraryConstants';
import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';
import SelectPropertyConstants from '../../../../constants/SelectPropertyConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { EXTERNAL_URLS } from '../../../../constants/urls';

import ApplicationItem from '../../../ApplicationsPage/components/ApplicationItem';
import ApplicationPermissionCard from '../../../ApplicationsPage/components/ApplicationPermissionCard';
import ApplicationsSummaryCard from '../../../ApplicationsPage/components/ApplicationsSummaryCard';
import ApplicationsPermissionSetupModal from '../../../modals/ApplicationsPermissionSetupModal';
import InviteToApplyModal from './InviteToApplyModal';

import ConnectedModuleCard from '../../../ConnectedModuleCard';
import EmptyState from '../../../EmptyState';
import SpotIllustration from '../../../EmptyState/SpotIllustration';

import type { Application } from '../../../ApplicationsPage/components/ApplicationItem';

type Props = {
  listingAlias: string;
  street?: string;
  propertyAddress?: { street?: string; city?: string; state?: string; zip?: string; unit?: string };
};

const ApplicationsModule = ({ listingAlias, street, propertyAddress }: Props) => {
  const [isInviteToApplyModalOpen, setIsInviteToApplyModalOpen] = useState(false);
  const [showAppsPermissionModal, setShowAppsPermissionModal] = useState(false);

  const { data, isPending } = useRentalManagerUserPropertySummaryWebApiHandler_applications(
    { listingAlias, getActive: true },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  const applicationsPath = selectPropertyUtils.getPathByType(
    SelectPropertyConstants.APPLICATIONS as keyof typeof SelectPropertyConstants,
    listingAlias,
  );

  const renderCTA = useMemo(() => {
    return (
      <TextButton tone="brand" asChild>
        <a href={applicationsPath || ''} target="_self">
          Details
        </a>
      </TextButton>
    );
  }, [applicationsPath]);

  const applicationList = data?.rentalApplicationData?.applicationList ?? [];
  const totalApplications = data?.rentalApplicationData?.totalNumberOfActiveApplications ?? 0;
  const hasApplications = totalApplications > 0;
  const isApplicationsAccepted = data?.isApplicationsAccepted ?? false;
  const isInviteOnly = data?.inviteOnly?.isInviteOnly ?? false;
  const isApplicationsUpdateRestricted = data?.restrictions?.isUpdateApplicationsRestricted;

  // Check user management flags for permission restrictions
  const userManagementFlags = data?.userManagementFlags;
  const isEligible = userManagementFlags?.isEligible ?? false;
  const canEnableApplications = userManagementFlags?.canEnableApplications ?? false;
  const canRequestAccess = userManagementFlags?.canRequestAccess ?? false;
  const isApplicationPermissionsEnabledForCompany =
    userManagementFlags?.isApplicationPermissionsEnabledForCompany ?? false;

  // Determine if user has permission restrictions (matches legacy logic)
  const hasNoPermission = isEligible && !canEnableApplications && !canRequestAccess;
  const needsToRequestAccess = isEligible && isApplicationPermissionsEnabledForCompany && canRequestAccess;
  const needsToSetApplicationManager =
    isEligible && !isApplicationPermissionsEnabledForCompany && canEnableApplications;

  const showSummaryCard = totalApplications > 4;
  const applicationsToRender = showSummaryCard ? applicationList.slice(0, 3) : applicationList;

  const handleSetApplicationManagerClick = () => {
    setShowAppsPermissionModal(true);
  };

  // Determine permission type for ApplicationPermissionCard
  let permissionType: keyof typeof PERMISSION_REQUEST_TYPE | null = null;
  if (hasNoPermission) {
    permissionType = PERMISSION_REQUEST_TYPE.NO_PERMISSION;
  } else if (needsToRequestAccess) {
    permissionType = PERMISSION_REQUEST_TYPE.REQUEST_ACCESS;
  } else if (needsToSetApplicationManager) {
    permissionType = PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER;
  }

  const renderContent = () => {
    if (isPending) {
      return null;
    }

    // Check permission restrictions first (before checking if applications are disabled)
    if (permissionType) {
      return (
        <ApplicationPermissionCard
          permissionType={permissionType}
          onSetApplicationManagerClick={handleSetApplicationManagerClick}
          applicationsSummary={data}
          listingAlias={listingAlias}
        />
      );
    }

    // Applications are disabled - show message to enable them
    if (!isApplicationsAccepted && !isInviteOnly) {
      return (
        <EmptyState
          data-testid="ApplicationsDisabledCard"
          useCard={false}
          align="left"
          headingLevel={4}
          icon={<SpotIllustration src={CLIPBOARD_ILLUSTRATION_URL} />}
          heading="Enable applications for your property"
          body="Allow renters to apply online. You’ll be able to view applications, request reports, and invite renters to apply."
          actions={
            isApplicationsUpdateRestricted ? (
              <Popover
                header={<Heading level={4}>Enable applications</Heading>}
                body={
                  <Paragraph>
                    Navigate to{' '}
                    <a href={EXTERNAL_URLS.STREETEASY_HOME} target="_blank" rel="noopener noreferrer">
                      StreetEasy
                    </a>{' '}
                    to control your applications preferences.
                  </Paragraph>
                }
                trigger={
                  <Button tone="brand" emphasis="filled">
                    Enable applications
                  </Button>
                }
              />
            ) : (
              <Button tone="brand" emphasis="filled" asChild>
                <a href={applicationsPath || ''} target="_self">
                  Enable applications
                </a>
              </Button>
            )
          }
        />
      );
    }

    // Applications are enabled but no applications yet
    if (!hasApplications) {
      return (
        <EmptyState
          data-testid="EmptyApplicationsCardSimple"
          useCard={false}
          align="left"
          headingLevel={4}
          icon={<SpotIllustration src={CLIPBOARD_ILLUSTRATION_URL} />}
          heading="You don’t have any applications yet"
          body="You can invite renters to apply or wait for renters to apply via your listing on Zillow Rental Network."
          actions={
            <Button tone="brand" emphasis="filled" onClick={() => setIsInviteToApplyModalOpen(true)}>
              Invite to apply
            </Button>
          }
        />
      );
    }

    // Has applications - show individual application cards
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(1, 1fr)',
            lg: applicationList.length < 2 ? 'repeat(1, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
          },
          gap: { base: '200', lg: '400' },
        }}
      >
        {applicationsToRender.map((application: Application, idx: number) => {
          return (
            <Box key={`application-${idx}`}>
              <ApplicationItem
                application={application}
                listingAlias={listingAlias}
                isLoading={false}
                displayType={VERTICAL_DISPLAY_TYPE.SIMPLE}
              />
            </Box>
          );
        })}

        {showSummaryCard && (
          <Box>
            <ApplicationsSummaryCard applicationsSummary={data} displayType={VERTICAL_DISPLAY_TYPE.SIMPLE} />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <ConnectedModuleCard
      data-testid="ApplicationsModule"
      title="Applications"
      ctaComponent={!isPending && (isApplicationsAccepted || isInviteOnly) && renderCTA}
      ctaFontType="body-sm"
      justifyHeadingContent={'center'}
      isLoading={isPending}
    >
      {renderContent()}

      <InviteToApplyModal
        listingAlias={listingAlias}
        street={street}
        open={isInviteToApplyModalOpen}
        onOpenChange={setIsInviteToApplyModalOpen}
      />

      {showAppsPermissionModal && (
        <ApplicationsPermissionSetupModal
          setShowApplicationsPermissionSetupModal={setShowAppsPermissionModal}
          propertyAddress={propertyAddress || { street }}
        />
      )}
    </ConnectedModuleCard>
  );
};

export default ApplicationsModule;
